import React, { useState, useEffect } from "react";
import { Spin, Button, Popconfirm, message, Empty, Tooltip } from "antd";
import { ReloadOutlined, CheckOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { NIVEIS, textoTempo } from "./useCheckinNotificacoes";

// ── Hook de responsividade (mesmo padrão usado no Dashboard) ────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Logo compacto do programa ────────────────────────────────────────────
// NOTA: o mapa completo de logos vive no componente de Emissão. O certo é
// extrair para utils/logos.js e importar nos dois lugares. Aqui vai o fallback.
const corPrograma = (nome = "") => {
  const n = nome.toLowerCase();
  if (n.includes("livelo"))  return "#e8008b";
  if (n.includes("smiles"))  return "#ff6600";
  if (n.includes("latam"))   return "#d81a36";
  if (n.includes("azul"))    return "#003087";
  if (n.includes("itaú") || n.includes("itau")) return "#d48c0c";
  if (n.includes("tap"))     return "#009900";
  if (n.includes("esfera"))  return "#8b0000";
  return "#607d8b";
};

const LogoCircle = ({ nome, size = 32 }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    background: corPrograma(nome), color: "#fff",
    fontSize: size * 0.34, fontWeight: 700, letterSpacing: "-0.02em",
  }}>
    {(nome || "??").replace(/[^a-zA-ZÀ-ÿ]/g, "").slice(0, 2).toUpperCase()}
  </span>
);

export const Notificacoes = ({ controller }) => {
  const isMobile = useIsMobile();

  // Componente controlado: todo o estado vem do hook no Dashboard.
  if (!controller) {
    return <div style={{ padding: 24, color: "#999" }}>Notificações indisponíveis.</div>;
  }
  const { ordenados, resumo, loading, marcando, carregar, marcarCheckin } = controller;

  const handleMarcar = async (venId) => {
    try {
      await marcarCheckin(venId);
      message.success("Check-in marcado como realizado.");
    } catch (e) {
      message.error(String(e?.response?.data || e?.message || "Erro ao marcar check-in"));
    }
  };

  const s = {
    card:   { background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "16px 16px 14px" : "18px 22px 16px", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap", gap: 10 },
    body:   { padding: isMobile ? 14 : 18 },
  };

  return (
    <div style={s.card}>
      {/* Cabeçalho */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>🔔</span>
          <div>
            <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "#222" }}>Notificações de Check-in</div>
            {!isMobile && (
              <div style={{ fontSize: 12, color: "#999", marginTop: 1 }}>
                Voos próximos em todos os clientes da gestão
              </div>
            )}
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading} size="middle">
          Atualizar
        </Button>
      </div>

      <div style={s.body}>
        {/* Barra de resumo por urgência */}
        {ordenados.length > 0 && (
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              { k: "aberto",     txt: "precisam de ação" },
              { k: "breve",      txt: "abrem em breve"    },
              { k: "programado", txt: "programados"       },
            ].map(({ k, txt }) => (
              <div key={k} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 14px", borderRadius: 8,
                background: NIVEIS[k].corBg, border: `1px solid ${NIVEIS[k].corBorda}`,
                flex: isMobile ? "1 1 100%" : "1 1 140px",
              }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: NIVEIS[k].cor, lineHeight: 1 }}>
                  {resumo[k]}
                </span>
                <span style={{ fontSize: 12, color: "#595959", lineHeight: 1.2 }}>{txt}</span>
              </div>
            ))}
          </div>
        )}

        <Spin spinning={loading}>
          {ordenados.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nenhum check-in pendente na janela. Tudo em dia. ✅"
              style={{ padding: "40px 0" }}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ordenados.map((v) => {
                const nv   = NIVEIS[v._cls.nivel];
                const hero = v._cls.nivel === "aberto";
                return (
                  <div
                    key={v.ven_id}
                    style={{
                      display: "flex",
                      // No mobile a barra fica em cima e a ação embaixo (linha),
                      // em vez de comprimir tudo numa única linha horizontal.
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: "stretch",
                      borderRadius: 8, overflow: "hidden",
                      border: `1px solid ${hero ? nv.corBorda : "#f0f0f0"}`,
                      background: hero ? nv.corBg : "#fff",
                      transition: "box-shadow .15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.09)")}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                  >
                    {/* Barra de urgência */}
                    <div style={{ width: isMobile ? "100%" : 4, height: isMobile ? 4 : "auto", background: nv.cor, flexShrink: 0 }} />

                    <div style={{ flex: 1, padding: isMobile ? "12px 14px" : "12px 16px", minWidth: 0 }}>
                      {/* Linha 1: urgência + tempo */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.04em", color: nv.cor,
                        }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: nv.cor }} />
                          {nv.label}
                        </span>
                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>· {textoTempo(v._cls.horas)}</span>
                      </div>

                      {/* Linha 2: localizador (herói) + rota */}
                      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, flexWrap: "wrap" }}>
                        <Tooltip title="Localizador — use para o check-in">
                          <span style={{
                            fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                            fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#111",
                            letterSpacing: "0.08em",
                            background: "#f5f5f5", padding: "2px 10px", borderRadius: 6,
                            border: "1px solid #ececec",
                          }}>
                            {v.ven_localizador || "—"}
                          </span>
                        </Tooltip>

                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: isMobile ? 13 : 14, color: "#333", fontWeight: 600 }}>
                          <EnvironmentOutlined style={{ color: "#bbb" }} />
                          {v.ven_origem || "?"}
                          <span style={{ color: "#bbb" }}>→</span>
                          {v.ven_destino || "?"}
                        </span>
                      </div>

                      {/* Linha 3: cliente + programa + data */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                          <LogoCircle nome={v.car_nome_programa} size={26} />
                          <span style={{ fontSize: 13, color: "#555" }}>
                            <strong style={{ color: "#333" }}>{v.cli_nome || "—"}</strong>
                            {v.car_nome_programa ? ` · ${v.car_nome_programa}` : ""}
                          </span>
                        </span>
                        <span style={{ fontSize: 13, color: "#8c8c8c", marginLeft: isMobile ? 0 : "auto" }}>
                          {v._cls.d ? v._cls.d.format("DD/MM/YYYY [às] HH:mm") : "data inválida"}
                        </span>
                      </div>
                    </div>

                    {/* Ação */}
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: isMobile ? "stretch" : "center",
                      padding: isMobile ? "10px 14px 14px" : "0 16px",
                      borderLeft: isMobile ? "none" : "1px solid #f5f5f5",
                      borderTop: isMobile ? "1px solid #f5f5f5" : "none",
                    }}>
                      <Popconfirm
                        title="Confirmar check-in?"
                        description="Isso marca este voo como check-in realizado e o remove da lista."
                        okText="Sim, marcar"
                        cancelText="Cancelar"
                        onConfirm={() => handleMarcar(v.ven_id)}
                      >
                        <Button
                          type={hero ? "primary" : "default"}
                          danger={hero}
                          icon={<CheckOutlined />}
                          loading={!!marcando[v.ven_id]}
                          block={isMobile}
                        >
                          Marcar check-in
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default Notificacoes;